import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { Pilot, PilotStatus } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { useAsync } from '@/lib/useAsync';

/** Pilot statuses that count as "still running" for the health summary —
 * everything before the terminal completed/scaled pair. */
const ACTIVE_STATUSES: readonly PilotStatus[] = [
  'draft',
  'baseline',
  'active',
  'interim_review',
  'final_review',
];

/**
 * "Organisatiegezondheid" (PLAN-FASE2.md WP-07): a company's existing data —
 * edition, member count — plus its pilot status from WP-04's `GET /pilots`,
 * composed on a single card. No new data source: this fetches nothing that
 * `CompanyDetailPage` (edition, member count) or `PilotsOverviewPage`
 * (`GET /pilots?company_id=`) don't already fetch elsewhere.
 */
export function OrganizationHealthCard({
  companyId,
  edition,
  memberCount,
}: {
  companyId: string;
  /** `undefined` while the edition field hasn't loaded / isn't set — falls back to "fit". */
  edition?: string | undefined;
  /** `undefined` while the member list hasn't loaded yet. */
  memberCount?: number | undefined;
}) {
  const { t } = useTranslation();
  const pilots = useAsync(
    () => apiGet('/pilots', { query: { company_id: companyId } }) as Promise<Pilot[]>,
    [companyId],
  );

  return (
    <Card aria-label={t('companies.health.title')}>
      <CardHeader
        title={t('companies.health.title')}
        action={
          <Link to={`/manage/pilots?company=${companyId}`} className="text-green text-sm">
            {t('companies.health.viewAllPilots')}
          </Link>
        }
      />
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-accent">{t('companies.health.edition')}</dt>
          <dd className="text-ink mt-1">
            <Badge tone="outline">
              {edition === 'soccer'
                ? t('companies.health.editionSoccer')
                : t('companies.health.editionFit')}
            </Badge>
          </dd>
        </div>
        <div>
          <dt className="text-accent">{t('companies.health.members')}</dt>
          <dd className="text-ink mt-1">{memberCount ?? '—'}</dd>
        </div>
      </dl>

      <div className="mt-4">
        <p className="text-accent text-sm">{t('companies.health.pilotsTitle')}</p>
        <AsyncSection state={pilots} label={t('companies.health.pilotsLabel')} skeleton="lines">
          {(rows) => {
            if (rows.length === 0) {
              return <p className="text-ink mt-1 text-sm">{t('companies.health.noPilots')}</p>;
            }
            const activeCount = rows.filter((pilot) =>
              ACTIVE_STATUSES.includes(pilot.status as PilotStatus),
            ).length;
            const scaledCount = rows.filter((pilot) => pilot.status === 'scaled').length;
            return (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone={activeCount > 0 ? 'mint' : 'outline'}>
                  {t('companies.health.activeCount', { count: activeCount })}
                </Badge>
                {scaledCount > 0 && (
                  <Badge tone="outline">
                    {t('companies.health.scaledCount', { count: scaledCount })}
                  </Badge>
                )}
                <span className="text-accent text-xs">
                  {t('companies.health.totalCount', { count: rows.length })}
                </span>
              </div>
            );
          }}
        </AsyncSection>
      </div>
    </Card>
  );
}
